import './utils/buttons.css';
import { isContractDisabled, getStatusColor } from './utils/contractStatus';
import { useSignal } from "@preact/signals-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import { useEffect, useState, useRef, Fragment } from "react";
import PageForm from "../../templates/PageForm";
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";
import CurrencyInput from "../../molecules/CurrencyInput";
import { applyDateMask } from '../../../utils/dateMask';
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage } from '../../../utils/formErrorUtils';
import { FaTimes, FaSearch, FaSave, FaBan, FaCheck, FaEllipsisV, FaRobot } from "react-icons/fa";
import BadgeStatus from "../../atoms/BadgeStatus";
import ReceivablesDownloader from "./components/ReceivablesDownloader";
import useProvinces from "../../../hooks/useProvinces";
import useCities from "../../../hooks/useCities";
import DuplicateModalView from "./modals/DuplicateModalView";
import ArtifactFilesUpload from './components/ArtifactFilesUpload';
import PersonSearchModal from "./modals/PersonSearchModal";
import AIContractFillModal from "./modals/AIContractFillModal";
import ConfirmationModal from "../../templates/ConfirmationModal";
import { DuplicateItem, InstallmentItem, GuarantorItem, StructuredOperationForm } from './types/operations';
import { PersonSearchResult, ContractApiResponse, ApiListResponse, PersonListItem, ProductResponse, ProvinceResponse, CityResponse, JsonUploadData } from './types/api-responses';
import DisbursementManager from './components/DisbursementManager';
import FeesManager from './components/FeesManager';
import InstallmentManager from './components/InstallmentManager';
import GuarantorsManager from './components/GuarantorsManager';
import FiduciaryAlienationManager from './components/FiduciaryAlienationManager';
import DuplicatesTable from './components/DuplicatesTable';
import { useFileDownload } from './components/useFileDownload';

const API_URL = process.env.REACT_APP_CONTRACTS_MANAGEMENT_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;
const OPERATIONS_LIST_URL = '/contracts/0/100/id/ASC/s=;';
const PERSON_SERVICE_URL = process.env.REACT_APP_PERSON_API_URL;

const isValidDate = (dateString: string): boolean => {
    if (!dateString || dateString.length !== 10) return false;
    const [day, month, year] = dateString.split('/');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.getDate() === parseInt(day) &&
        date.getMonth() === parseInt(month) - 1 &&
        date.getFullYear() === parseInt(year);
};

const reprocessInstallmentNumbers = (installments: InstallmentItem[]) => {
    const sortedInstallments = [...installments].sort((a, b) => {
        const dateA = new Date(a.dueDate.split('/').reverse().join('-'));
        const dateB = new Date(b.dueDate.split('/').reverse().join('-'));
        return dateA.getTime() - dateB.getTime();
    });
    return sortedInstallments.map((installment, index) => ({
        ...installment,
        number: index + 1
    }));
};

const loadGuarantorsFromParticipants = (participants?: any[]) => {
    if (!participants) {
        return { items: [], quantity: 0 };
    }

    const guarantorParticipants = participants.filter(p => p.participant_type_name === 'GUARANTOR');

    return {
        items: guarantorParticipants.map((p: any) => ({
            id: p.id ? String(p.id) : undefined,
            uuid: p.person_id || '',
            name: p.name || '',
            document: p.document || '',
            entity_type: p.entity_type || 'FIDEJUSSÓRIA'
        })),
        quantity: guarantorParticipants.length
    };
};

const loadFees = (fees: any) => {
    if (!fees) {
        return { items: [], quantity: 0 };
    }

    // Old format: array of fees
    if (Array.isArray(fees)) {
        return {
            items: fees.map((fee: any) => ({
                name: fee.fee_name || fee.name || '',
                rate: fee.rate || 0,
                amount: parseFloat(fee.fee_value || fee.amount) || 0,
                form: fee.form || '',
                interest_period: fee.interest_period || ''
            })),
            quantity: fees.length
        };
    }

    // New format: object with items and quantity
    return {
        items: (fees.items || []).map((fee: any) => ({
            name: fee.fee_name || fee.name || '',
            rate: fee.rate || 0,
            amount: parseFloat(fee.fee_value || fee.amount) || 0,
            form: fee.form || '',
            interest_period: fee.interest_period || ''
        })),
        quantity: fees.quantity || (fees.items || []).length
    };
};

const StructuredOperationPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const operationType = searchParams.get('operation_type') || 'STRUCTURED_OPERATION';
    const credit_operation_id = params.credit_operation_id || params.id;
    const { handleDownload } = useFileDownload();
    const artifactFiles = useSignal<File[]>([]);
    const contractFiles = useSignal<File[]>([]);
    const [existingContractFile, setExistingContractFile] = useState<string>('');
    const [existingArtifactFiles, setExistingArtifactFiles] = useState<string[]>([]);
    const [removedContractFile, setRemovedContractFile] = useState<boolean>(false);
    const [removedArtifactFiles, setRemovedArtifactFiles] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;
    const [selectedBeneficiary, setSelectedBeneficiary] = useState('');
    const { fetchProvinces } = useProvinces();
    const { fetchCities } = useCities();
    const [provinces, setProvinces] = useState<Array<{ value: string, label: string, initials: string }>>([]);
    const [cities, setCities] = useState<Array<{ value: string, label: string }>>([]);
    const [selectedDuplicate, setSelectedDuplicate] = useState<DuplicateItem | null>(null);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [lastInstallmentDueDate, setLastInstallmentDueDate] = useState<string | undefined>(undefined);
    const apiVersion = useSignal('');

    const form = useSignal<StructuredOperationForm>({
        assignor: '',
        recipient: '',
        total_amount: 0,
        expiration_date: '',
        emission_date: '',
        operation_number: '',
        external_link: '',
        rebate_amount: 0,
        guarantee_value: null,
        status: '',
        sequencial_number: 0,
        originator: '',
        operation_uf: '',
        operation_city: '',
        with_recourse: false,
        description_product: {
            name: '',
            id: ''
        },
        duplicates: {
            quantity: 0,
            total: 0,
            items: []
        },
        disbursement: {
            totalAmount: 0,
            disbursements: []
        },
        installments: [],
        installment_format: 'VALOR',
        guarantors: {
            items: [],
            quantity: 0
        },
        main_risk: {
            uuid: '',
            name: ''
        },
        fees: {
            items: [],
            quantity: 0
        },
        fiduciary_alienation: [],
        artifacts: [],
        signed_contract_file: {
            file_id: '',
            file_name: ''
        }
    });

    const isLoading = useSignal(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [isAIModalOpen, setIsAIModalOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleCancelContract = async () => {
        try {
            isLoading.value = true;
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${API_URL}/contracts/${credit_operation_id}/status/CANCELED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato cancelado com sucesso!');
                navigate('/contracts/0/100/id/ASC/s=;');
            } else {
                toast.error('Erro ao cancelar contrato');
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
        } finally {
            isLoading.value = false;
            setIsCancelModalOpen(false);
        }
    };

    const handleFinishContract = async () => {
        try {
            isLoading.value = true;
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const response = await fetch(`${API_URL}/contracts/${credit_operation_id}/status/FINISHED`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || ''
                }
            });
            if (response.ok) {
                toast.success('Contrato finalizado com sucesso!');
                navigate('/contracts/0/100/id/ASC/s=;');
            } else {
                toast.error('Erro ao finalizar contrato');
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
        } finally {
            isLoading.value = false;
            setIsFinishModalOpen(false);
        }
    };
    const errorFields = useSignal<ErrorField[]>([]);
    const [assignors, setAssignors] = useState<Array<{ value: string; label: string }>>([]);
    const [recipients, setRecipients] = useState<Array<{ value: string; label: string }>>([]);
    const [searchResults, setSearchResults] = useState<Array<{ value: string, label: string, personData?: PersonSearchResult }>>([]);
    const [searchDestinatario, setSearchDestinatario] = useState('');
    const [selectedDestinatario, setSelectedDestinatario] = useState('');
    const [recipientData, setRecipientData] = useState<PersonSearchResult | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [guarantors, setGuarantors] = useState<Array<{ value: string; label: string; entity_type: string }>>([]);
    const [products, setProducts] = useState<Array<{ value: string; label: string }>>([]);
    const [assignorParticipant, setAssignorParticipant] = useState<{ uuid: string; name: string; document: string; id: number | null }>({ uuid: '', name: '', document: '', id: null });
    const [recipientParticipant, setRecipientParticipant] = useState<{ uuid: string; name: string; document: string; id: number | null }>({ uuid: '', name: '', document: '', id: null });
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [newProduct, setNewProduct] = useState({ name: '', description: '', quality_standard: '' });
    const [isCreatingProduct, setIsCreatingProduct] = useState(false);

    const handleSelectParticipant = (selected: PersonSearchResult) => {
        console.log('🔵 Beneficiário selecionado:', {
            uuid: selected.uuid,
            name: selected.name,
            documents: selected.documents
        });

        setSelectedBeneficiary(selected.uuid);
        setSelectedDestinatario(selected.uuid);
        form.value = { ...form.value, recipient: selected.name || '' };

        const cnpjDoc = selected.documents?.find((doc) => doc.type === 'CNPJ');
        const cpfDoc = selected.documents?.find((doc) => doc.type === 'CPF');
        const document = cnpjDoc?.number || cpfDoc?.number || '';
        setSearchDestinatario(document);

        setRecipientData(selected);
        setSearchResults([]);

        console.log('✅ selectedBeneficiary atualizado para:', selected.uuid);
    };

    const handleSelectPerson = (selected: PersonSearchResult) => {
        handleSelectParticipant(selected);
        setIsModalOpen(false);
    };


    const fetchContractData = async () => {
        if (!credit_operation_id) {
            return;
        }
        if (!API_URL || !API_KEY) {
            return;
        }

        try {
            isLoading.value = true;
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());
            const response = await fetch(`${API_URL}/contracts/receivables_assignment/${credit_operation_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || ''
                }
            });

            if (response.ok) {
                const data: ContractApiResponse = await response.json();
                const schema = data.operation_dictionary || {};

                // Set API version info if available
                if (data.apiName && data.apiVersion) {
                    apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
                }

                const assignorPart = data.participants?.find((p) => p.participant_type_name === 'ASSIGNOR');
                const recipientParticipant = data.participants?.find((p) => p.participant_type_name === 'RECIPIENT');

                if (assignorPart) {
                    setAssignorParticipant({
                        uuid: assignorPart.person_id || '00000000-0000-0000-0000-000000000000',
                        name: assignorPart.name || '',
                        document: assignorPart.document || '',
                        id: assignorPart.id ? Number(assignorPart.id) : null
                    });
                }

                if (recipientParticipant) {
                    setSelectedBeneficiary(recipientParticipant.person_id || '00000000-0000-0000-0000-000000000000');
                    setRecipientParticipant({
                        uuid: recipientParticipant.person_id || '00000000-0000-0000-0000-000000000000',
                        name: recipientParticipant.name || '',
                        document: recipientParticipant.document || '',
                        id: recipientParticipant.id ? Number(recipientParticipant.id) : null
                    });
                }

                form.value = {
                    ...form.value,
                    operation_number: data.operation_number || '',
                    status: data.status || '',
                    sequencial_number: data.sequencial_number || 0,
                    assignor: assignorPart?.name || data.assignor || '',
                    recipient: recipientParticipant?.name || data.recipient || '',
                    originator: data.originator || '',
                    with_recourse: data.with_recourse || false,
                    operation_uf: schema.operation_uf || '',
                    operation_city: schema.operation_city || '',
                    total_amount: schema.total_amount || data.nominal_value || 0,
                    expiration_date: schema.expiration_date ? schema.expiration_date.split('T')[0].split('-').reverse().join('/') : '',
                    emission_date: schema.emission_date ? schema.emission_date.split('T')[0].split('-').reverse().join('/') : '',
                    external_link: schema.external_link || '',
                    rebate_amount: schema.rebate_amount || 0,
                    description_product: schema.description_product || { name: '', id: '' },
                    duplicates: {
                        quantity: schema.duplicates?.quantity || 0,
                        total: schema.duplicates?.total || 0,
                        items: (schema.duplicates?.items || []).map((item: any) => ({
                            cfop: item.cfop || '',
                            issue_date: item.issue_date || item.document_date || '',
                            debtor_name: item.debtor_name || '',
                            document_id: item.document_id || item.document_number || '',
                            address_city: item.address_city || '',
                            debtor_email: item.debtor_email || '',
                            address_phone: item.address_phone || '',
                            address_state: item.address_state || '',
                            assignor_cnpj: item.assignor_cnpj || item.assignor_document || '',
                            assignor_name: item.assignor_name || '',
                            address_number: item.address_number || '',
                            address_street: item.address_street || '',
                            document_title: item.document_title || item.document_type || '',
                            xml_size_bytes: item.xml_size_bytes || 0,
                            address_country: item.address_country || '',
                            debtor_cpf_cnpj: item.debtor_cpf_cnpj || item.debtor_document || '',
                            address_district: item.address_district || '',
                            address_zip_code: item.address_zip_code || '',
                            receivable_v_pag: item.receivable_v_pag || '',
                            address_ibge_code: item.address_ibge_code || '',
                            receivable_amount: parseFloat(item.receivable_amount || item.receivable_value) || 0,
                            receivable_nat_op: item.receivable_nat_op || '',
                            receivable_number: item.receivable_number || item.document_number || '',
                            address_complement: item.address_complement || '',
                            receivable_ind_pag: item.receivable_ind_pag || '',
                            receivable_due_date: item.receivable_due_date || '',
                            disbursement_value: parseFloat(item.disbursement_value || '0'),
                            fee_name: item.fee_name || '',
                            fee_value: parseFloat(item.fee_value || '0'),
                            guarantee_percent: parseFloat(item.guarantee_percent || '0'),
                            main_risk: item.main_risk || '',
                            external_link: item.external_link || ''
                        }))
                    },
                    disbursement: {
                        totalAmount: (schema as any).disbursement?.totalAmount || schema.total_amount || data.nominal_value || 0,
                        disbursements: (schema as any).disbursement?.disbursements?.map((d: any) => ({
                            amount: d.amount || 0,
                            date: d.date ? new Date(d.date).toLocaleDateString('pt-BR') : ''
                        })) || []
                    },
                    installments: reprocessInstallmentNumbers(
                        (schema as any).installment?.items?.map((inst: any, index: number) => ({
                            number: index + 1,
                            value: inst.value || inst.amount || 0,

                            dueDate: inst.dueDate ? new Date(inst.dueDate).toLocaleDateString('pt-BR') : '',
                            format: inst.format || 'VALOR'

                        })) || []
                    ),
                    guarantee_value: schema.guarantee_value || null,
                    guarantors: loadGuarantorsFromParticipants(data.participants),
                    main_risk: typeof schema.main_risk === 'string' ? { uuid: '', name: schema.main_risk } : (schema.main_risk || { uuid: '', name: '' }),
                    fees: loadFees(schema.fees),
                    fiduciary_alienation: (Array.isArray(schema.fiduciary_alienation) ? schema.fiduciary_alienation : []).map((item: any) => ({
                        name: item.name || '',
                        value: item.value || 0,
                        description: item.description || undefined,
                        guarantee_type: item.guarantee_type || 'AF'
                    })),
                    artifacts: schema.artifacts || [],
                    signed_contract_file: schema.signed_contract_file || { file_id: '', file_name: '' }
                };

                // Load existing files from operation_dictionary
                if (schema.files) {
                    setExistingContractFile(schema.files.contract_file || '');
                    setExistingArtifactFiles(schema.files.artefacts || []);
                }

            } else {
                toast.error('Erro ao carregar dados da operação');
            }
        } catch (error) {
            toast.error('Erro ao conectar com o servidor');
        } finally {
            isLoading.value = false;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());

                const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY || ''
                    }
                });
                if (response.ok) {
                    const data: ApiListResponse<PersonListItem> = await response.json();
                    const items = data.results.map((item) => ({
                        value: item.uuid,
                        label: `${item.name} - ${item.documents?.find((doc) => doc.type === 'CNPJ')?.number || ''}`
                    }));

                    setAssignors(items);
                    setRecipients(items);
                    const guarantorItems = data.results.map((item) => {
                        const cnpjDoc = item.documents?.find((doc) => doc.type === 'CNPJ');
                        const cpfDoc = item.documents?.find((doc) => doc.type === 'CPF');
                        const document = cnpjDoc?.number || cpfDoc?.number || '';
                        return {
                            value: item.uuid,
                            label: `${item.name} - ${document}`,
                            entity_type: item.personType === 'LEGAL_PERSON' ? 'PJ' : 'PF'
                        };
                    });
                    setGuarantors(guarantorItems);
                }
            } catch (error) {
            }
        };

        const fetchProducts = async () => {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());

                const response = await fetch(`${API_URL}/product`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY || ''
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    // Novo payload: { products: [...] }
                    const products = data.products || data;
                    setProducts(products.map((product: ProductResponse) => ({
                        value: product.uuid,
                        label: product.name
                    })));
                }
            } catch (error) {
            }
        };

        const fetchProvincesData = async () => {
            try {
                const provincesData: ProvinceResponse[] = await fetchProvinces();
                const provinceItems = provincesData.map((item: ProvinceResponse) => ({
                    value: item.initials,
                    label: item.name,
                    initials: item.initials
                }));
                setProvinces(provinceItems);
                return provinceItems;
            } catch (error) {
                return [];
            }
        };

        const loadDataSequentially = async () => {
            const provincesData = await fetchProvincesData();

            if (credit_operation_id) {
                await fetchContractData();
                // Após carregar os dados do contrato, buscar cidades se UF estiver selecionada
                if (form.value.operation_uf) {
                    const selectedProvince = provincesData.find((p: { value: string; label: string; initials: string }) => p.value === form.value.operation_uf);

                    if (selectedProvince) {
                        fetchCitiesWrapper(selectedProvince.initials);
                    }
                }
            }
        };

        fetchData();
        fetchProducts();
        loadDataSequentially();
    }, [credit_operation_id]);

    // Update duplicates when external_link or main_risk changes
    useEffect(() => {
        if (form.value.duplicates?.items?.length > 0) {
            const updatedItems = form.value.duplicates.items.map(item => ({
                ...item,
                main_risk: form.value.main_risk?.name || "",
                external_link: form.value.external_link || ""
            }));
            form.value = {
                ...form.value,
                duplicates: {
                    ...form.value.duplicates,
                    items: updatedItems
                }
            };
        }
    }, [form.value.external_link, form.value.main_risk?.name]);

    const handleProvinceChange = async (initials: string) => {
        fetchCitiesWrapper(initials);
        if (initials) {
            const selectedProvince = provinces.find(p => p.initials === initials);
            if (selectedProvince) {

            }
            form.value = {
                ...form.value,
                operation_uf: initials,
                operation_city: ''
            };
        } else {
            setCities([]);
            form.value = {
                ...form.value,
                operation_uf: '',
                operation_city: ''
            };
        }
    };

    const fetchCitiesWrapper = async (initials: string) => {
        try {
            if (initials) {

                const citiesData: CityResponse[] = await fetchCities(initials);
                const cityItems = citiesData.map((item: CityResponse) => ({
                    value: item.name,
                    label: item.name
                }));
                setCities(cityItems);
            }
        } catch (error) {
        }
    };

    const updateDuplicatesWithFormData = (items: DuplicateItem[]): DuplicateItem[] => {
        return items.map(item => ({
            ...item,
            main_risk: form.value.main_risk?.name || "",
            external_link: form.value.external_link || ""
        }));
    };

    const handleJsonDataLoaded = (data: JsonUploadData) => {
        if (data.duplicates?.items) {
            const mappedItems: DuplicateItem[] = data.duplicates.items.map((item: any) => ({
                document_id: item.document_id || '',
                document_title: item.document_title || '',
                cfop: item.cfop || '',
                issue_date: item.issue_date || '',
                xml_size_bytes: item.xml_size_bytes || 0,
                assignor_name: item.assignor_name || '',
                assignor_cnpj: item.assignor_cnpj || '',
                debtor_cpf_cnpj: item.debtor_cpf_cnpj || '',
                debtor_name: item.debtor_name || '',
                debtor_email: item.debtor_email || '',
                address_street: item.address_street || '',
                address_number: item.address_number || '',
                address_complement: item.address_complement || '',
                address_district: item.address_district || '',
                address_ibge_code: item.address_ibge_code || '',
                address_city: item.address_city || '',
                address_state: item.address_state || '',
                address_zip_code: item.address_zip_code || '',
                address_country: item.address_country || '',
                address_phone: item.address_phone || '',
                receivable_number: item.receivable_number || '',
                receivable_due_date: item.receivable_due_date || '',
                receivable_amount: item.receivable_amount || 0,
                receivable_v_pag: item.receivable_v_pag || '',
                receivable_nat_op: item.receivable_nat_op || '',
                receivable_ind_pag: item.receivable_ind_pag || '',
                disbursement_value: item.disbursement_value || 0,
                fee_name: item.fee_name || '',
                fee_value: item.fee_value || 0,
                guarantee_percent: item.guarantee_percent || 0,
                main_risk: form.value.main_risk?.name || '',
                external_link: form.value.external_link || ''
            }));
            const updatedItems = updateDuplicatesWithFormData(mappedItems);
            form.value = {
                ...form.value,
                operation_number: data.operation_code || form.value.operation_number,
                duplicates: {
                    quantity: data.duplicates.quantity || data.duplicates.items.length,
                    total: data.duplicates.total || 0,
                    items: updatedItems
                }
            };
        }
    };

    const isFormDisabled = isContractDisabled(form.value.status);

    const generateParticipants = () => {
        const recipientDocument = recipientData?.documents?.find((doc) => doc.type === 'CNPJ')?.number ||
            recipientData?.documents?.find((doc) => doc.type === 'CPF')?.number ||
            recipientParticipant.document || '';

        return [
            {
                id: assignorParticipant.id,
                name: form.value.assignor,
                document: assignorParticipant.document || "",
                is_active: true,
                person_id: assignorParticipant.uuid || "00000000-0000-0000-0000-000000000000",
                is_primary: true,
                effective_to: null,
                effective_from: new Date().toISOString(),
                guarantee_value: null,
                participant_type_id: 3,
                participation_order: 1,
                guarantee_percentage: null,
                participant_type_name: "ASSIGNOR",
                receives_disbursement: false
            },
            {
                id: recipientParticipant.id,
                name: form.value.recipient,
                document: recipientDocument,
                is_active: true,
                person_id: selectedBeneficiary,
                is_primary: false,
                effective_to: null,
                effective_from: new Date().toISOString(),
                guarantee_value: null,
                participant_type_id: 5,
                participation_order: 2,
                guarantee_percentage: null,
                participant_type_name: "RECIPIENT",
                receives_disbursement: false
            },
            // Adicionar avalistas como participants com tipo GUARANTOR
            ...form.value.guarantors.items.map((guarantor, index) => ({
                id: guarantor.id ? Number(guarantor.id) : null,
                name: guarantor.name,
                document: guarantor.document,
                is_active: true,
                person_id: guarantor.uuid,
                is_primary: false,
                effective_to: null,
                effective_from: new Date().toISOString(),
                guarantee_value: null,
                participant_type_id: 2,
                participation_order: 3 + index,
                guarantee_percentage: null,
                participant_type_name: "GUARANTOR",
                receives_disbursement: false
            }))
        ];
    };

    const handleCreateProduct = async () => {
        if (!newProduct.name.trim()) {
            toast.error('Nome é obrigatório');
            return;
        }

        try {
            setIsCreatingProduct(true);
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const response = await fetch(`${API_URL}/product`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: newProduct.name,
                    description: newProduct.description,
                    quality_standard: newProduct.quality_standard
                })
            });

            if (response.ok) {
                const createdProduct = await response.json();
                toast.success('Produto criado com sucesso!');
                setIsProductModalOpen(false);
                setNewProduct({ name: '', description: '', quality_standard: '' });

                // Recarregar lista de produtos
                const token2 = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());
                const productsResponse = await fetch(`${API_URL}/product`, {
                    headers: {
                        'Authorization': `Bearer ${token2}`,
                        'x-api-key': API_KEY || ''
                    }
                });
                if (productsResponse.ok) {
                    const data = await productsResponse.json();
                    // Novo payload: { products: [...] }
                    const productsList = data.products || data;
                    const newProducts = productsList.map((product: ProductResponse) => ({
                        value: product.uuid,
                        label: product.name
                    }));
                    setProducts(newProducts);

                    // Selecionar automaticamente o produto criado
                    if (createdProduct?.uuid) {
                        form.value = {
                            ...form.value,
                            description_product: {
                                id: createdProduct.uuid,
                                name: createdProduct.name
                            }
                        };
                    }
                }
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao criar produto');
            }
        } catch (error) {
            console.error('Erro ao criar produto:', error);
            toast.error('Erro ao conectar com o servidor ao criar produto');
        } finally {
            setIsCreatingProduct(false);
        }
    };

    const handleSubmit = async () => {
        if (isFormDisabled) {
            toast.error('Não é possível salvar. Status não permite alterações.');
            return;
        }

        if (!API_URL || !API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        if (!selectedBeneficiary || selectedBeneficiary === '00000000-0000-0000-0000-000000000000') {
            toast.error('Selecione um beneficiário');
            return;
        }

        // Validar Total dos Desembolsos obrigatório e maior que 0
        if (!form.value.disbursement.totalAmount || form.value.disbursement.totalAmount <= 0) {
            toast.error('Valor Nominal dos Desembolsos deve ser maior que 0');
            return;
        }

        // Validar se soma dos desembolsos não excede o Valor Nominal
        const sumDisbursements = form.value.disbursement.disbursements.reduce((sum, d) => sum + d.amount, 0);
        if (sumDisbursements > form.value.disbursement.totalAmount) {
            toast.error(`Soma dos desembolsos (R$ ${sumDisbursements.toFixed(2)}) não pode exceder o Valor Nominal (R$ ${form.value.disbursement.totalAmount.toFixed(2)})`);
            return;
        }

        const sanitizeFileName = (fileName: string): string => {
            return fileName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
        };

        try {
            isLoading.value = true;

            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            // Construir payload conforme JSON schema
            const dueDateValue = lastInstallmentDueDate || form.value.expiration_date || null;

            const payload = {
                type: operationType || "STRUCTURED_OPERATION",
                assignor: "00000000-0000-0000-0000-000000000000",
                recipient: selectedBeneficiary,
                credit_operation_id: credit_operation_id,
                operation_number: form.value.operation_number || "",
                originator: form.value.originator || "",
                with_recourse: form.value.with_recourse || false,
                operation_type: "STRUCTURED_OPERATION",
                operation_schema_id: 3,
                approved_on: null,
                approved_value: null,
                current_balance: null,
                debt_balance_id: null,
                nominal_value: form.value.disbursement.totalAmount || 0,
                due_date: dueDateValue,
                expiration_date: form.value.expiration_date || null,
                participants: generateParticipants(),
                operation_dictionary: {
                    assignor: "00000000-0000-0000-0000-000000000000",
                    recipient: selectedBeneficiary,
                    emission_date: form.value.emission_date ? form.value.emission_date.split('/').reverse().join('-') : "",
                    guarantee_value: form.value.guarantee_value,
                    rebate_amount: form.value.rebate_amount || 0,
                    fees: {
                        items: form.value.fees.items.map(fee => ({
                            rate: fee.rate || 0,
                            amount: fee.amount || 0,
                            form: fee.form || "",
                            name: fee.name || "",
                            interest_period: fee.interest_period || ""
                        })),
                        quantity: form.value.fees.items.length
                    },
                    fiduciary_alienation: form.value.fiduciary_alienation.map(item => ({
                        name: item.name || "",
                        value: item.value || 0,
                        guarantee_type: item.guarantee_type || "AF",
                        ...(item.description && { description: item.description })
                    })),
                    disbursement: {
                        disbursements: form.value.disbursement.disbursements.map(d => ({
                            amount: d.amount || 0,
                            date: d.date ? d.date.split('/').reverse().join('-') : ""
                        })),
                        totalAmount: form.value.disbursement.totalAmount || 0
                    },
                    installment: {
                        total: form.value.installments.reduce((sum, inst) => sum + inst.value, 0),
                        lastDueDate: form.value.installments.length > 0
                            ? form.value.installments.sort((a, b) => new Date(b.dueDate.split('/').reverse().join('-')).getTime() - new Date(a.dueDate.split('/').reverse().join('-')).getTime())[0].dueDate.split('/').reverse().join('-')
                            : "",
                        items: form.value.installments.map(inst => ({
                            value: inst.value || 0,
                            dueDate: inst.dueDate ? inst.dueDate.split('/').reverse().join('-') : "",
                            format: inst.format || "VALOR"
                        }))
                    },
                    duplicates: {
                        items: form.value.duplicates?.items?.map(item => ({
                            document_id: item.document_id || "",
                            document_title: item.document_title || "",
                            cfop: item.cfop || "",
                            issue_date: item.issue_date || "",
                            xml_size_bytes: item.xml_size_bytes || 0,
                            assignor_name: item.assignor_name || "",
                            assignor_cnpj: item.assignor_cnpj || "",
                            debtor_cpf_cnpj: item.debtor_cpf_cnpj || "",
                            debtor_name: item.debtor_name || "",
                            debtor_email: item.debtor_email || "",
                            address_street: item.address_street || "",
                            address_number: item.address_number || "",
                            address_complement: item.address_complement || "",
                            address_district: item.address_district || "",
                            address_ibge_code: item.address_ibge_code || "",
                            address_city: item.address_city || "",
                            address_state: item.address_state || "",
                            address_zip_code: item.address_zip_code || "",
                            address_country: item.address_country || "",
                            address_phone: item.address_phone || "",
                            receivable_number: item.receivable_number || "",
                            receivable_due_date: item.receivable_due_date || "",
                            receivable_amount: item.receivable_amount || 0,
                            receivable_v_pag: item.receivable_v_pag || "",
                            receivable_nat_op: item.receivable_nat_op || "",
                            receivable_ind_pag: item.receivable_ind_pag || "",
                            disbursement_value: item.disbursement_value || 0,
                            fee_name: item.fee_name || "",
                            fee_value: item.fee_value || 0,
                            guarantee_percent: item.guarantee_percent || 0,
                            main_risk: item.main_risk || "",
                            external_link: item.external_link || ""
                        })) || [],
                        total: form.value.duplicates?.total || 0,
                        quantity: form.value.duplicates?.quantity || 0
                    },
                    operation_number: form.value.operation_number || "",
                    external_link: form.value.external_link || "",
                    operation_uf: form.value.operation_uf || 0,
                    operation_city: form.value.operation_city || "",
                    main_risk: {
                        uuid: form.value.main_risk.uuid || "",
                        name: form.value.main_risk.name || ""
                    },
                    credit_product_type: "STRUCTURED_OPERATION",
                    is_settled: false,
                    artifacts: [
                        {
                            list: "DOCUMENTOS",
                            files: artifactFiles.value.map(file => sanitizeFileName(file.name))
                        }
                    ],
                    description_product: {
                        id: form.value.description_product?.id || "",
                        name: form.value.description_product?.name || ""
                    },
                    files: {
                        contract_file: contractFiles.value.length > 0
                            ? `${form.value.sequencial_number}${sanitizeFileName(contractFiles.value[0].name.substring(contractFiles.value[0].name.lastIndexOf('.')))}`
                            : (removedContractFile ? '' : existingContractFile),
                        artefacts: [
                            ...existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)),
                            ...artifactFiles.value.map(file => sanitizeFileName(file.name))
                        ]
                    }
                }
            };

            const headers: Record<string, string> = {
                'Authorization': `Bearer ${token}`,
                'x-api-key': API_KEY || '',
                'Content-Type': 'application/json'
            };

            const url = `${API_URL}/contracts/receivables_assignment/update`;

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Response data:', data);

                // Upload files via presigned URLs in parallel
                if (data.upload_urls && Object.keys(data.upload_urls).length > 0) {
                    const uploadEntries = Object.entries(data.upload_urls);
                    toast.info(`Enviando ${uploadEntries.length} arquivo(s)...`);

                    const uploadPromises = uploadEntries.map(async ([fileName, uploadInfo]: [string, any]) => {
                        let fileToUpload: File | undefined;

                        if (contractFiles.value.length > 0) {
                            fileToUpload = contractFiles.value[0];
                        } else {
                            fileToUpload = artifactFiles.value.find(f => fileName.includes(sanitizeFileName(f.name)));
                        }
                        if (fileToUpload) {
                            console.log(`Uploading file: ${fileName} to ${uploadInfo.upload_url}`);
                            const uploadResponse = await fetch(uploadInfo.upload_url, {
                                method: 'PUT',
                                body: fileToUpload,
                                headers: {
                                    'Content-Type': 'application/octet-stream'
                                }
                            });
                            console.log(`Upload response status: ${uploadResponse.status}`);
                            if (!uploadResponse.ok) {
                                console.error(`Upload failed for ${fileName}:`, await uploadResponse.text());
                                throw new Error(`Upload failed for ${fileName}`);
                            }
                            return uploadResponse;
                        }
                    });

                    await Promise.all(uploadPromises);
                    toast.success('Arquivos enviados com sucesso!');
                } else {
                    console.log('No upload_urls in response');
                }

                const message = credit_operation_id
                    ? "Operação estruturada atualizada com sucesso!"
                    : "Operação estruturada criada com sucesso!";
                toast.success(message);

                if (credit_operation_id) {
                    await fetchContractData();
                    contractFiles.value = [];
                    artifactFiles.value = [];
                    setRemovedContractFile(false);
                    setRemovedArtifactFiles([]);
                } else {
                    navigate(-1);
                }
            } else {
                const errorData: { message?: string; validationMessages?: Array<{ fieldName: string; code: string }> } = await response.json();
                toast.error(errorData.message || 'Erro ao salvar operação');
                errorFields.value = errorData.validationMessages?.map((item: { fieldName: string; code: string }) => ({
                    fieldName: item.fieldName,
                    message: item.code
                })) || [];
            }
        } catch (error) {
            toast.error("Erro ao conectar com o servidor");
        } finally {
            isLoading.value = false;
        }
    };

    return (
        <Fragment>
            <PageForm
                pageTitle="Operação Estruturada"
                apiVersion={apiVersion.value}
                isLoading={isLoading.value}
                breadcrumb={[
                    { label: 'Contratos', url: OPERATIONS_LIST_URL },
                    { label: 'Operação Estruturada', url: '#' }
                ]}
                controlSlot={
                    <div className="pt-4 flex justify-end">
                        <button
                            id="btn-ai-assistant"
                            className="px-4 py-2 bg-white border-2 border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 dark:bg-slate-800 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => setIsAIModalOpen(true)}
                            disabled={isLoading.value}
                        >
                            <FaRobot className="h-4 w-4" />
                            Assistente IA
                        </button>

                        <button
                            className="btn-cancel ml-2"
                            onClick={() => navigate(-1)}
                            disabled={isLoading.value}
                        >
                            Voltar
                        </button>

                        {/* Dropdown de Ações */}
                        <div ref={dropdownRef} className="relative inline-block text-left ml-2">
                            <button
                                type="button"
                                className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                disabled={isLoading.value}
                            >
                                Ações
                                <FaEllipsisV className="ml-2 h-4 w-4" />
                            </button>

                            {isDropdownOpen && (
                                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                                    <div className="py-1">
                                        <button
                                            className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => {
                                                handleSubmit();
                                                setIsDropdownOpen(false);
                                            }}
                                            disabled={isLoading.value || isFormDisabled}
                                        >
                                            <FaSave className="mr-3 h-4 w-4 text-green-500" />
                                            Salvar
                                        </button>
                                        <button
                                            className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => {
                                                setIsCancelModalOpen(true);
                                                setIsDropdownOpen(false);
                                            }}
                                            disabled={isLoading.value || !credit_operation_id || isFormDisabled}
                                        >
                                            <FaBan className="mr-3 h-4 w-4 text-orange-500" />
                                            Cancelar Contrato
                                        </button>
                                        <button
                                            className="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => {
                                                setIsFinishModalOpen(true);
                                                setIsDropdownOpen(false);
                                            }}
                                            disabled={isLoading.value || !credit_operation_id || isFormDisabled}
                                        >
                                            <FaCheck className="mr-3 h-4 w-4 text-green-500" />
                                            Finalizar Contrato
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                }
            >
                {/* Renderizar ReceivablesDownloader apenas se não há duplicatas existentes */}
                {(!form.value.duplicates?.items || form.value.duplicates.items.length === 0) && (
                    <ReceivablesDownloader
                        sequencialNumber={form.value.sequencial_number || 0}
                        duplicatesCount={0}
                        onDataLoaded={handleJsonDataLoaded}
                    />
                )}
                <div className="container mx-auto px-2 py-4">
                    <h2 className="text-xl font-semibold mt-4 mb-4">Informações Gerais</h2>
                    <div className="mb-4">
                        <BadgeStatus
                            color={getStatusColor(form.value.status)}
                            text={form.value.status || ''}
                        />
                    </div>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-2">
                            <TextEdit
                                id="sequencial_number"
                                label="Número do Contrato"
                                value={form.value.sequencial_number?.toString() || ''}
                                disabled={true}
                            />
                        </div>
                    </div>

                    <h2 className="text-xl font-semibold mt-4 mb-4">Dados Gerais</h2>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <TextEdit
                                id="operation_number"
                                label="Número da Operação"
                                value={form.value.operation_number}
                                onChange={(e) => form.value = { ...form.value, operation_number: e.target.value }}
                                errorMessage={getErrorMessage(errorFields.value, "operation_number")}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="external_link"
                                label="Card JIRA"
                                value={form.value.external_link}
                                onChange={(e) => form.value = { ...form.value, external_link: e.target.value }}
                                errorMessage={getErrorMessage(errorFields.value, "external_link")}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <TextEdit
                                id="originador"
                                label="Originador"
                                value={form.value.originator}
                                onChange={(e) => form.value = { ...form.value, originator: e.target.value }}
                                errorMessage={getErrorMessage(errorFields.value, "originator")}
                            />
                        </div>
                        <div className="col-span-2">
                            <SelectTextField
                                id="operation_uf"
                                label="UF da Operação"
                                value={form.value.operation_uf.toString()}
                                items={provinces}
                                onChange={(e) => handleProvinceChange(e.target.value)}
                                errorMessage={getErrorMessage(errorFields.value, "operation_uf")}
                            />
                        </div>
                        <div className="col-span-2">
                            <SelectTextField
                                id="operation_city"
                                label="Cidade da Operação"
                                value={form.value.operation_city}
                                items={cities}
                                onChange={(e) => form.value = { ...form.value, operation_city: e.target.value }}
                                errorMessage={getErrorMessage(errorFields.value, "operation_city")}
                            />
                        </div>
                        <div className="col-span-2 flex items-center pt-6">
                            <input
                                type="checkbox"
                                id="with_recourse"
                                checked={form.value.with_recourse}
                                onChange={(e) => form.value = { ...form.value, with_recourse: e.target.checked }}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <label htmlFor="with_recourse" className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                Com Regresso
                            </label>
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <TextEdit
                                id="assignor"
                                label="Emitente"
                                value={form.value.assignor}
                                onChange={(e) => form.value = { ...form.value, assignor: e.target.value }}
                                errorMessage={getErrorMessage(errorFields.value, "assignor")}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="emission_date"
                                label="Data de Emissão"
                                value={form.value.emission_date}
                                onChange={(e) => form.value = { ...form.value, emission_date: applyDateMask(e.target.value) }}
                                onBlur={(e) => {
                                    if (!isValidDate(e.target.value)) {
                                        toast.error('Data de Emissão inválida');
                                        e.target.focus();
                                    }
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "emission_date")}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 mb-6">
                        <div className="col-span-7">
                            <TextEdit
                                id="recipient"
                                label="Beneficiário"
                                value={form.value.recipient}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-1 flex items-end">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 mb-4 h-10 w-[75px] flex items-center justify-center"
                                title="Buscar beneficiário"
                            >
                                <FaSearch />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 mb-6">
                        <div className="col-span-7">
                            <SelectTextField
                                id="product_id"
                                label="Produto"
                                value={form.value.description_product?.id || ''}
                                items={products}
                                onChange={(e) => {
                                    const selected = products.find(product => product.value === e.target.value);
                                    form.value = {
                                        ...form.value,
                                        description_product: {
                                            id: e.target.value,
                                            name: selected?.label || ''
                                        }
                                    };
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "product")}
                            />
                        </div>
                        <div className="col-span-1 flex items-end">
                            <button
                                onClick={() => setIsProductModalOpen(true)}
                                className="btn-add mb-4 h-10 w-[75px]"
                                title="Novo produto"
                            >
                                Novo
                            </button>
                        </div>
                    </div>

                    <DisbursementManager
                        disbursements={form.value.disbursement.disbursements}
                        totalAmount={form.value.disbursement.totalAmount}
                        onTotalAmountChange={(value) => {
                            form.value = {
                                ...form.value,
                                total_amount: value,
                                disbursement: {
                                    ...form.value.disbursement,
                                    totalAmount: value
                                }
                            };
                        }}
                        onAdd={(disbursement) => {
                            const currentDisbursements = [...form.value.disbursement.disbursements, disbursement];
                            form.value = {
                                ...form.value,
                                disbursement: { ...form.value.disbursement, disbursements: currentDisbursements }
                            };
                        }}
                        onRemove={(index) => {
                            const disbursements = form.value.disbursement.disbursements.filter((_, i) => i !== index);
                            form.value = {
                                ...form.value,
                                disbursement: { ...form.value.disbursement, disbursements }
                            };
                        }}
                        disabled={isLoading.value}
                    />

                    <FeesManager
                        fees={form.value.fees}
                        onAdd={(fee) => {
                            const newItems = [...form.value.fees.items, fee];
                            form.value = {
                                ...form.value,
                                fees: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                        }}
                        onRemove={(index) => {
                            const newItems = form.value.fees.items.filter((_, i) => i !== index);
                            form.value = {
                                ...form.value,
                                fees: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                        }}
                        disabled={isLoading.value}
                    />

                    <h2 className="text-xl font-semibold mt-4 mb-4">Vencimento</h2>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <CurrencyInput
                                id="total_amount"
                                label="Valor Nominal"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.total_amount}
                                onChange={(value) => form.value = { ...form.value, total_amount: value }}
                                errorMessage={getErrorMessage(errorFields.value, "total_amount")}
                                disabled={false}
                            />
                        </div>
                        {form.value.with_recourse && (
                            <div className="col-span-3">
                                <TextEdit
                                    id="expiration_date"
                                    label="Data de vencimento"
                                    value={form.value.expiration_date}
                                    onChange={(e) => form.value = { ...form.value, expiration_date: applyDateMask(e.target.value) }}
                                    onBlur={(e) => {
                                        if (!isValidDate(e.target.value)) {
                                            toast.error('Data de vencimento inválida');
                                            e.target.focus();
                                        }
                                    }}
                                    errorMessage={getErrorMessage(errorFields.value, "expiration_date")}
                                    disabled={!form.value.installments.some(inst => inst.format === 'PERCENTUAL')}
                                />
                            </div>
                        )}
                    </div>

                    <InstallmentManager
                        installments={form.value.installments}
                        nominalValue={form.value.disbursement.totalAmount}
                        onAdd={(installment) => {
                            form.value = {
                                ...form.value,
                                installments: [...form.value.installments, installment]
                            };
                        }}
                        onRemove={(index) => {
                            form.value = {
                                ...form.value,
                                installments: form.value.installments.filter((_, i) => i !== index)
                            };
                        }}
                        onUpdate={(updatedInstallments, lastDueDate) => {
                            setLastInstallmentDueDate(lastDueDate);
                            form.value = {
                                ...form.value,
                                installments: updatedInstallments,
                                expiration_date: lastDueDate || form.value.expiration_date
                            };
                        }}
                        disabled={isLoading.value}
                    />

                    <GuarantorsManager
                        guarantors={form.value.guarantors}
                        guaranteeValue={form.value.guarantee_value}
                        onGuaranteeValueChange={(value) => form.value = { ...form.value, guarantee_value: value }}
                        onAdd={(guarantor) => {
                            const newItems = [...form.value.guarantors.items, guarantor];
                            form.value = {
                                ...form.value,
                                guarantors: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                        }}
                        onRemove={(index) => {
                            const newItems = form.value.guarantors.items.filter((_, i) => i !== index);
                            form.value = {
                                ...form.value,
                                guarantors: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                        }}
                        disabled={isLoading.value}
                    />

                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-6">
                            <SelectTextField
                                id="main_risk_uuid"
                                label="Risco Principal"
                                value={form.value.main_risk.uuid}
                                items={[
                                    { value: assignorParticipant.uuid || '00000000-0000-0000-0000-000000000000', label: assignorParticipant.name || form.value.assignor || 'Emitente' },
                                    ...form.value.guarantors.items.map(g => ({ value: g.uuid, label: g.name }))
                                ]}
                                onChange={(e) => {
                                    if (e.target.value === 'assignor') {
                                        form.value = {
                                            ...form.value,
                                            main_risk: {
                                                uuid: '00000000-0000-0000-0000-000000000000',
                                                name: form.value.assignor || 'Emitente'
                                            }
                                        };
                                    } else {
                                        const selected = form.value.guarantors.items.find((g: GuarantorItem) => g.uuid === e.target.value);
                                        form.value = {
                                            ...form.value,
                                            main_risk: {
                                                uuid: e.target.value,
                                                name: selected?.name || ''
                                            }
                                        };
                                    }
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "main_risk")}
                            />
                        </div>
                    </div>

                    <FiduciaryAlienationManager
                        items={form.value.fiduciary_alienation}
                        onAdd={(item) => {
                            form.value = {
                                ...form.value,
                                fiduciary_alienation: [...form.value.fiduciary_alienation, item]
                            };
                        }}
                        onRemove={(index) => {
                            form.value = {
                                ...form.value,
                                fiduciary_alienation: form.value.fiduciary_alienation.filter((_, i) => i !== index)
                            };
                        }}
                        disabled={isLoading.value}
                    />

                    <h2 className="text-xl font-semibold mt-4 mb-4">Contrato</h2>
                    <div>
                        <div 
                            className="flex items-center justify-center w-full"
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (e.dataTransfer.files?.[0]) {
                                    contractFiles.value = [e.dataTransfer.files[0]];
                                }
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            <label htmlFor="contract-upload"
                                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                                    </svg>
                                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        <span className="font-semibold">Upload do Contrato</span>
                                    </p>
                                </div>
                                <input
                                    id="contract-upload"
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            contractFiles.value = [file];
                                        }
                                    }}
                                />
                            </label>
                        </div>
                        {existingContractFile && !removedContractFile && (
                            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-800 border border-blue-200 dark:border-blue-600 rounded flex justify-between items-center">
                                <p className="text-sm text-blue-700 dark:text-blue-100">
                                    <strong>Arquivo atual:</strong>{' '}
                                    <button
                                        onClick={() => handleDownload(credit_operation_id || '', existingContractFile)}
                                        className="underline hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer bg-transparent border-0 p-0"
                                    >
                                        {existingContractFile}
                                    </button>
                                </p>
                                <button
                                    onClick={() => setRemovedContractFile(true)}
                                    className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                                >
                                    <FaTimes className="inline mr-1" /> Remover
                                </button>
                            </div>
                        )}
                        {removedContractFile && (
                            <div className="mt-2 p-2 bg-red-50 dark:bg-red-800 border border-red-200 dark:border-red-600 rounded flex justify-between items-center">
                                <p className="text-sm text-red-700 dark:text-red-100">
                                    <strong>Arquivo removido:</strong> {existingContractFile}
                                </p>
                                <button
                                    onClick={() => setRemovedContractFile(false)}
                                    className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                                >
                                    Restaurar
                                </button>
                            </div>
                        )}
                        {contractFiles.value.length > 0 && (
                            <div className="mt-2 p-2 bg-green-50 dark:bg-green-800 border border-green-200 dark:border-green-600 rounded">
                                <p className="text-sm text-green-700 dark:text-green-100">
                                    <strong>Novo arquivo:</strong> {contractFiles.value[0].name}
                                </p>
                            </div>
                        )}
                    </div>

                    <ArtifactFilesUpload
                        artifactFiles={artifactFiles}
                        existingFiles={existingArtifactFiles}
                        removedFiles={removedArtifactFiles}
                        onRemoveExisting={(file) => setRemovedArtifactFiles([...removedArtifactFiles, file])}
                        onRestoreExisting={(file) => setRemovedArtifactFiles(removedArtifactFiles.filter(f => f !== file))}
                        creditOperationId={credit_operation_id || ''}
                        onDownload={handleDownload}
                    />

                    <DuplicatesTable
                        duplicates={form.value.duplicates?.items || []}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onRowClick={(duplicate) => {
                            setSelectedDuplicate(duplicate);
                            setIsDuplicateModalOpen(true);
                        }}
                    />
                </div>

                <PersonSearchModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSelect={handleSelectPerson}
                />

                {/* Modal de criação de produto */}
                {isProductModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold dark:text-white">Novo Produto</h3>
                                <button
                                    onClick={() => {
                                        setIsProductModalOpen(false);
                                        setNewProduct({ name: '', description: '', quality_standard: '' });
                                    }}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="space-y-4">
                                <TextEdit
                                    label="Nome"
                                    value={newProduct.name}
                                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                                />
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
                                    <textarea
                                        rows={4}
                                        value={newProduct.description}
                                        onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                                        className="block w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent border border-gray-300 dark:border-gray-600 rounded p-2 focus:outline-none focus:ring-0 focus:border-blue-600 dark:focus:border-blue-400 resize-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Padrão de Qualidade</label>
                                    <textarea
                                        rows={4}
                                        value={newProduct.quality_standard}
                                        onChange={(e) => setNewProduct({ ...newProduct, quality_standard: e.target.value })}
                                        className="block w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent border border-gray-300 dark:border-gray-600 rounded p-2 focus:outline-none focus:ring-0 focus:border-blue-600 dark:focus:border-blue-400 resize-none"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    onClick={() => {
                                        setIsProductModalOpen(false);
                                        setNewProduct({ name: '', description: '', quality_standard: '' });
                                    }}
                                    className="px-4 py-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                                    disabled={isCreatingProduct}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCreateProduct}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isCreatingProduct}
                                >
                                    {isCreatingProduct ? 'Criando...' : 'Criar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}


            </PageForm>
            
            {/* Modal de IA */}
            <AIContractFillModal
                isOpen={isAIModalOpen}
                onClose={() => setIsAIModalOpen(false)}
                onSubmit={async (result) => {
                    console.log('=== RESULTADO DO PROCESSAMENTO ===');
                    console.log('Resultado completo:', result);
                    
                    try {
                        isLoading.value = true;
                        
                        const opDict = result.operation_dictionary;
                        
                        // Função para buscar person_id por documento
                        const findPersonByDocument = async (document: string) => {
                            const token = await Auth.currentSession()
                                .then((user) => user.getIdToken().getJwtToken());
                            
                            const cleanDoc = document.replace(/\D/g, '');
                            const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${cleanDoc}`, {
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'x-api-key': API_KEY || ''
                                }
                            });
                            
                            if (response.ok) {
                                const data = await response.json();
                                if (data.results && data.results.length > 0) {
                                    return data.results[0].uuid;
                                }
                            }
                            return null;
                        };
                        
                        // Buscar person_id para cada participante (exceto ASSIGNOR)
                        const participantsWithPersonId = await Promise.all(
                            opDict.participants
                                .filter((p: any) => p.participant_type_name !== 'ASSIGNOR')
                                .map(async (p: any) => {
                                    const personId = await findPersonByDocument(p.document);
                                    console.log(`Participante ${p.name} (${p.document}): person_id = ${personId}`);
                                    return {
                                        person_id: personId || '',
                                        participant_type: p.participant_type_name,
                                        name: p.name,
                                        document: p.document
                                    };
                                })
                        );
                        
                        // Encontrar participantes específicos (ignorar ASSIGNOR)
                        const recipient = participantsWithPersonId.find(p => p.participant_type === 'RECIPIENT');
                        
                        // Atualizar estado do recipient
                        if (recipient) {
                            setRecipientParticipant({
                                uuid: recipient.person_id,
                                name: recipient.name,
                                document: recipient.document,
                                id: null
                            });
                            setSelectedBeneficiary(recipient.person_id);
                        }
                        
                        // Formatar data de DD/MM/YYYY
                        const formatDate = (dateStr: string) => {
                            if (!dateStr) return '';
                            const [year, month, day] = dateStr.split('-');
                            return `${day}/${month}/${year}`;
                        };
                        
                        // Formatar desembolsos
                        const disbursements = opDict.disbursement?.disbursements?.map((d: any) => ({
                            date: formatDate(d.date),
                            amount: d.amount
                        })) || [];
                        
                        // Formatar parcelas
                        const installments = opDict.installment?.items?.map((inst: any, index: number) => ({
                            number: index + 1,
                            value: inst.value || 0,
                            dueDate: formatDate(inst.dueDate),
                            format: inst.format || 'VALOR'
                        })) || [];
                        
                        // Formatar taxas
                        const fees = opDict.fees?.items?.map((fee: any) => ({
                            name: fee.description || fee.name || '',
                            rate: fee.rate || 0,
                            amount: fee.value || 0,
                            form: fee.form || '',
                            interest_period: fee.interest_period || ''
                        })) || [];
                        
                        // Buscar produto pelo nome
                        const productName = opDict.product || '';
                        const foundProduct = products.find(p => p.label.toLowerCase() === productName.toLowerCase());
                        
                        console.log('Produto buscado:', productName);
                        console.log('Produto encontrado:', foundProduct);
                        
                        // Atualizar formulário
                        form.value = {
                            ...form.value,
                            operation_number: result.operation_number || '',
                            total_amount: result.nominal_value || 0,
                            expiration_date: formatDate(result.expiration_date),
                            emission_date: formatDate(opDict.emission_date),
                            with_recourse: result.with_recourse || false,
                            recipient: recipient?.name || '',
                            description_product: {
                                id: foundProduct?.value || '',
                                name: foundProduct?.label || productName
                            },
                            duplicates: {
                                quantity: opDict.duplicates?.quantity || 0,
                                total: opDict.duplicates?.total || 0,
                                items: opDict.duplicates?.items || []
                            },
                            disbursement: {
                                totalAmount: opDict.disbursement?.totalAmount || 0,
                                disbursements: disbursements
                            },
                            installments: installments,
                            fees: {
                                items: fees,
                                quantity: fees.length
                            }
                        };
                        
                        console.log('Participantes com person_id:', participantsWithPersonId);
                        console.log('Formulário preenchido:', form.value);
                        toast.success('Formulário preenchido com sucesso!');
                        
                    } catch (error) {
                        console.error('Erro ao buscar participantes:', error);
                        toast.error('Erro ao buscar dados dos participantes');
                    } finally {
                        isLoading.value = false;
                    }
                }}
            />
            
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onCancel={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelContract}
                id="cancel-contract-modal"
                title="Cancelar Contrato"
                text="Tem certeza que deseja cancelar este contrato?"
            />
            <ConfirmationModal
                isOpen={isFinishModalOpen}
                onCancel={() => setIsFinishModalOpen(false)}
                onConfirm={handleFinishContract}
                id="finish-contract-modal"
                title="Finalizar Contrato"
                text="Tem certeza que deseja finalizar este contrato?"
            />
            <DuplicateModalView
                isOpen={isDuplicateModalOpen}
                onClose={() => {
                    setIsDuplicateModalOpen(false);
                    setSelectedDuplicate(null);
                }}
                duplicate={selectedDuplicate}
            />
        </Fragment>);
};

export default StructuredOperationPage;